import { z } from 'zod'

// ─── Promoções por item (App cliente · F0, issue #203; spec §8.2) ─────────────
//
// `percent`: inteiro 1–90 (mesma escala de combos.discount_value: 20 = 20%).
// `fixed_price`: PREÇO FINAL em centavos — a regra "< preço base" depende do
// produto e é validada no servidor (features/promotions).

export const PROMOTION_DISCOUNT_TYPES = ['percent', 'fixed_price'] as const
export type PromotionDiscountTypeValue = (typeof PROMOTION_DISCOUNT_TYPES)[number]

export const PROMOTION_PERCENT_MIN = 1
export const PROMOTION_PERCENT_MAX = 90
export const PROMOTION_LABEL_MAX = 60

/** Bitmask de dias: bit 0 = segunda … bit 6 = domingo (ordem da UI do admin). */
export const PROMOTION_DAYS = {
  mon: 1,
  tue: 2,
  wed: 4,
  thu: 8,
  fri: 16,
  sat: 32,
  sun: 64,
} as const
export const PROMOTION_ALL_DAYS_MASK = 127

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/

const PromotionBaseShape = {
  product_id: z.string().uuid(),
  variant_id: z.string().uuid().nullable().optional(),
  discount_type: z.enum(PROMOTION_DISCOUNT_TYPES),
  discount_value: z.number().int().min(1),
  starts_at: z.number().int().positive().nullable().optional(),
  ends_at: z.number().int().positive().nullable().optional(),
  days_of_week: z.number().int().min(1).max(PROMOTION_ALL_DAYS_MASK).nullable().optional(),
  start_time: z.string().regex(HHMM, 'Use o formato HH:MM').nullable().optional(),
  end_time: z.string().regex(HHMM, 'Use o formato HH:MM').nullable().optional(),
  is_active: z.boolean().default(true),
  label: z.string().trim().min(1).max(PROMOTION_LABEL_MAX).nullable().optional(),
}

type PromotionRuleInput = {
  discount_type?: PromotionDiscountTypeValue | undefined
  discount_value?: number | undefined
  variant_id?: string | null | undefined
  starts_at?: number | null | undefined
  ends_at?: number | null | undefined
  start_time?: string | null | undefined
  end_time?: string | null | undefined
}

const has = <T>(v: T | null | undefined): v is T => v !== null && v !== undefined

/** Regras que não dependem do banco (as que dependem, ficam no serviço). */
function refinePromotion(v: PromotionRuleInput, ctx: z.RefinementCtx): void {
  if (v.discount_type === 'percent') {
    if (
      v.discount_value !== undefined &&
      (v.discount_value < PROMOTION_PERCENT_MIN || v.discount_value > PROMOTION_PERCENT_MAX)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discount_value'],
        message: `Percentual deve estar entre ${PROMOTION_PERCENT_MIN} e ${PROMOTION_PERCENT_MAX}`,
      })
    }
    if (v.variant_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['variant_id'],
        message: 'Promoção percentual vale para todas as variantes (variant_id deve ser nulo)',
      })
    }
  }
  if (has(v.starts_at) && has(v.ends_at) && v.starts_at >= v.ends_at) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['ends_at'],
      message: 'ends_at deve ser depois de starts_at',
    })
  }
  if (has(v.start_time) !== has(v.end_time)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['end_time'],
      message: 'Informe start_time e end_time juntos (ou nenhum dos dois)',
    })
  } else if (has(v.start_time) && v.start_time === v.end_time) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['end_time'],
      message: 'start_time e end_time não podem ser iguais',
    })
  }
}

export const PromotionCreateSchema = z.object(PromotionBaseShape).superRefine(refinePromotion)

/**
 * Atualização parcial. `product_id` não muda (crie outra promoção). Mudança
 * de tipo exige reenviar o valor: o serviço revalida o conjunto final.
 */
export const PromotionUpdateSchema = z
  .object({
    ...PromotionBaseShape,
    product_id: z.never().optional(),
    discount_type: PromotionBaseShape.discount_type.optional(),
    discount_value: PromotionBaseShape.discount_value.optional(),
    is_active: z.boolean().optional(),
  })
  .superRefine(refinePromotion)

export type PromotionCreateInput = z.infer<typeof PromotionCreateSchema>
export type PromotionUpdateInput = z.infer<typeof PromotionUpdateSchema>
